// Client wrapper for the Greenhouse recurring-reminder engine.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ReminderCategory =
  | "marketing" | "community" | "operations" | "financial" | "relationships" | "ministry";

export type ReminderPriority = "low" | "medium" | "high";
export type ReminderStatus = "active" | "completed" | "dismissed";

export type Recurrence =
  | { type: "interval_days"; days: number }
  | { type: "annual"; month: number; day: number }
  | null;

export interface ChecklistItem {
  label: string;
  done: boolean;
}

export interface CompletionEntry {
  completed_at: string;
  completed_by: string;
  notes: string | null;
  due_date: string;
}

export interface GreenhouseReminder {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  category: ReminderCategory;
  priority: ReminderPriority;
  due_date: string;
  recurrence: Recurrence;
  checklist: ChecklistItem[];
  action_label: string | null;
  action_kind: string | null;
  action_href: string | null;
  status: ReminderStatus;
  completed_at: string | null;
  completion_history: CompletionEntry[];
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("greenhouse-engine", { body });
  if (error) throw error;
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as T;
}

export function useDueReminders() {
  return useQuery({
    queryKey: ["greenhouse", "due"],
    queryFn: () => call<{ reminders: GreenhouseReminder[] }>({ action: "list_due" }),
    staleTime: 60_000,
  });
}

export function useAllReminders() {
  return useQuery({
    queryKey: ["greenhouse", "all"],
    queryFn: () => call<{ reminders: GreenhouseReminder[] }>({ action: "list_all" }),
    staleTime: 60_000,
  });
}

export function useCompleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { reminder_id: string; notes?: string }) =>
      call<{ reminder: GreenhouseReminder }>({ action: "complete_reminder", ...args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["greenhouse"] }),
  });
}

export function useSnoozeReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { reminder_id: string; days?: number }) =>
      call({ action: "snooze_reminder", ...args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["greenhouse"] }),
  });
}

export function useDismissReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { reminder_id: string }) =>
      call({ action: "dismiss_reminder", ...args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["greenhouse"] }),
  });
}

export function useToggleChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { reminder_id: string; index: number; done: boolean }) =>
      call<{ reminder: GreenhouseReminder }>({ action: "toggle_checklist_item", ...args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["greenhouse"] }),
  });
}

export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: Partial<GreenhouseReminder> & { title: string; due_date: string }) =>
      call<{ reminder: GreenhouseReminder }>({ action: "create_reminder", ...args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["greenhouse"] }),
  });
}

export const CATEGORY_META: Record<ReminderCategory, { emoji: string; label: string }> = {
  marketing: { emoji: "📣", label: "Marketing" },
  community: { emoji: "🌎", label: "Community" },
  operations: { emoji: "📚", label: "Operations" },
  financial: { emoji: "💵", label: "Financial" },
  relationships: { emoji: "❤️", label: "Relationships" },
  ministry: { emoji: "🙏", label: "Ministry" },
};

export const PRIORITY_META: Record<ReminderPriority, { label: string; className: string }> = {
  high: { label: "High priority", className: "bg-primary/10 text-primary border-primary/30" },
  medium: { label: "Medium priority", className: "bg-secondary/10 text-secondary border-secondary/30" },
  low: { label: "Low priority", className: "bg-muted text-muted-foreground border-border" },
};

export function describeRecurrence(r: Recurrence): string {
  if (!r) return "One-time";
  if (r.type === "interval_days") return `Repeats every ${r.days} days`;
  if (r.type === "annual") {
    const d = new Date(Date.UTC(2000, r.month - 1, r.day));
    return `Repeats every year on ${d.toLocaleDateString(undefined, { month: "long", day: "numeric", timeZone: "UTC" })}`;
  }
  return "";
}
